import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaPhone, FaWhatsapp } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import logo from '../../../assets/images/logo.svg';
import LanguageSwitcher from '../../../features/common/LanguageSwitcher';
import CartBadge from '../../../features/shop/components/CartBadge';
import './Navbar.css';
import config from "../../../config/config";

interface NavbarProps {
  onQuoteClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onQuoteClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const headerRef = useRef<HTMLElement>(null);

  // The header is fixed, so Layout offsets the page by its height. Publish the
  // measured height instead of hardcoding it — the top bar changes height with
  // the viewport, and a stale constant leaves a white gap under the navbar.
  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    // Sum the two bars rather than measuring the header: once scrolled,
    // .main-nav is fixed and leaves the header's flow, and the header also
    // contains the full-height mobile menu when it is open.
    const bars = ['.top-bar', '.main-nav'].map((selector) =>
      header.querySelector<HTMLElement>(selector)
    );

    const publishHeight = () => {
      const height = bars.reduce((total, bar) => total + (bar?.offsetHeight ?? 0), 0);
      document.documentElement.style.setProperty('--navbar-height', `${height}px`);
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    bars.forEach((bar) => bar && observer.observe(bar));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // No padding here: framer-motion keeps a value that only one variant declares,
  // so scrolling down and back left 10px of inline padding on the header — a
  // white strip under the nav. It was invisible while scrolled anyway, since
  // .main-nav.scrolled is fixed at top 0 and the top bar is faded out.
  const navVariants = {
    hidden: { y: -100 },
    visible: { y: 0, transition: { duration: 0.5 } },
    scrolled: { y: 0, transition: { duration: 0.3 } }
  };

  const topBarVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.5, delay: 0.3 } },
    scrolled: { opacity: 0,boxShadow: '0 0.1px 1px rgba(0, 0, 0, 0.6)',    transition: { duration: 0.3 } }
  };

  const mainNavVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.5, delay: 0.5 } }
  };

  const logoVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5, delay: 0.7 } },
    scrolled: { opacity: 1, scale: 1, transition: { duration: 0.3 } }
  };

  const menuItemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { delay: 0.8 + i * 0.1 }
    })
  };

  const quoteButtonVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0, transition: { delay: 1.2 } }
  };

  const mobileMenuVariants = {
    closed: { x: '100%' },
    open: { x: 0, transition: { type: 'spring', stiffness: 300, damping: 30 } }
  };

  const menuItems = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/projects', label: t('nav.projects') },
    { path: '/boutique', label: t('nav.shop', 'Boutique') },
    { path: '/blog', label: t('nav.blog') },
    { path: '/contact', label: t('nav.contact') },
  ];

  // Exact match for the home link, prefix match elsewhere so detail pages
  // (e.g. /boutique/mon-produit) keep their section highlighted.
  const isActivePath = (path: string): boolean =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const handleNavLinkClick = () => {
    window.scrollTo(0, 0);
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.header
      ref={headerRef}
      className="navbar-container"
      initial="hidden"
      animate={isScrolled ? 'scrolled' : 'visible'}
      variants={navVariants}
    >
      <motion.div
        className="top-bar"
        initial="hidden"
        animate={isScrolled ? 'scrolled' : 'visible'}
        variants={topBarVariants}
      >
        <div className="top-bar-content ">
          <div className="contact-info"  >
            <div className="contact-info-phone">
            <a href={`tel:${config.contact.phone}`} className="contact-item" style={{ direction: 'ltr' }}>
              <FaPhone /> {config.contact.phone}
            </a>
            <a href={`https://api.whatsapp.com/send?phone=${config.contact.whatsapp}&text=${encodeURIComponent(config.contact.whatsappMessage)}`} target="_blank" rel="noopener noreferrer" className="contact-item contact-item-whatsapp" >
              <FaWhatsapp /> {t('whatsapp.title')}
            </a>
            </div>


            <a href={`mailto:${config.contact.email}`} className="contact-item">
              <MdEmail /> {config.contact.email}
            </a>
          </div>
          <div className="top-bar-right">
            <LanguageSwitcher />
          </div>
        </div>
      </motion.div>
      
      <motion.nav
        className={`main-nav ${isScrolled ? 'scrolled' : ''}`}
        initial="hidden"
        animate="visible"
        variants={mainNavVariants}
      >
        <div className="nav-content">
          <motion.div
            className="logo-container"
            initial="hidden"
            animate={isScrolled ? 'scrolled' : 'visible'}
            variants={logoVariants}
          >
            <Link to="/" onClick={handleNavLinkClick}>
              <img src={logo} alt="A2S SARL Logo" />
            </Link>
          </motion.div>
          
          <div className="menu-items">
            {menuItems.map((item, index) => (
              <motion.div
                key={item.path}
                className="menu-item"
                custom={index}
                initial="hidden"
                animate="visible"
                variants={menuItemVariants}
              >
                <Link
                  to={item.path}
                  className={`menu-link ${isActivePath(item.path) ? 'active' : ''}`}
                  onClick={handleNavLinkClick}
                >
                  {item.label}
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="nav-cart"
            variants={quoteButtonVariants}
            initial="hidden"
            animate="visible"
          >
            <CartBadge onClick={handleNavLinkClick} />
          </motion.div>

          <motion.button
            className="quote-button"
            variants={quoteButtonVariants}
            initial="hidden"
            animate="visible"
            onClick={() => {
              if (onQuoteClick) {
                onQuoteClick();
              } else {
                window.location.href = '/quote';
                window.scrollTo(0, 0);
              }
            }}
          >
            {t('nav.quote')}
          </motion.button>
          
          <motion.button
            className="menu-button"
            onClick={() => setIsMobileMenuOpen(true)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Ouvrir le menu"
          >
            <FaBars />
          </motion.button>
        </div>
      </motion.nav>
      
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="mobile-menu"
            initial="closed"
            animate="open"
            exit="closed"
            variants={mobileMenuVariants}
          >
            <div className="mobile-menu-header">
              <div></div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: 'var(--text-color)' }}
              >
                <FaTimes />
              </motion.button>
            </div>
            
            <div className="mobile-menu-items">
              {menuItems.map((item) => (
                <div key={item.path} className="mobile-menu-item">
                  <Link
                    to={item.path}
                    className={isActivePath(item.path) ? 'active' : ''}
                    onClick={handleNavLinkClick}
                  >
                    {item.label}
                  </Link>
                </div>
              ))}
            </div>

            <motion.button
              className="mobile-quote-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                if (onQuoteClick) {
                  onQuoteClick();
                } else {
                  window.location.href = '/quote';
                  window.scrollTo(0, 0);
                }
              }}
            >
              {t('nav.quote')}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
